import { AdSurgeLogoText, Income, ReportUp } from '@tencent/odc-iconpack';
import classNames from 'classnames';
import { Button, DropMenu, Tabs } from 'odc-shared/components';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { SET_GET_IN_TOUCH } from '@src/constants/common';
import { useSkipAnimation } from '@src/hooks/useSkipAnimation';

import './index.scss';

interface HeaderProps {
  onPageChange?: (page: number | string) => void;
  onLoginClick?: (product: string) => void;
}

const Header = (props: HeaderProps) => {
  const { onPageChange, onLoginClick } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const [page, setPage] = useState<number | string>('products');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // 记录是否离开过 products 页面
  const skipAnimation = useSkipAnimation(page, 'products');
  // 检测当前页面路径，更新选中状态，更新onPageChange
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const current = searchParams.get('current');
    if (current && current !== page) {
      setPage(current);
      onPageChange?.(current);
    }
  }, [location.search, onPageChange, page]);

  // 打开 drawer 时禁止页面滚动，关闭时恢复
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    if (mobileMenuOpen) {
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      html.style.position = 'fixed';
      html.style.width = '100%';
      html.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = html.style.top;
      html.style.overflow = '';
      body.style.overflow = '';
      html.style.position = '';
      html.style.width = '';
      html.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
    }
    return () => {
      html.style.overflow = '';
      body.style.overflow = '';
      html.style.position = '';
      html.style.width = '';
      const scrollY = html.style.top;
      html.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY, 10) * -1);
      }
    };
  }, [mobileMenuOpen]);

  const handleMobileNavClick = (val: string) => {
    setPage(val);
    navigate(`?current=${val}`);
    onPageChange?.(val);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <div className={classNames('odc-oversea-official-header', `page-${page}-header`, { 'skip-animation': skipAnimation })}>
        <div className="logo">
          <AdSurgeLogoText className="logo-brand" />
        </div>
        <div className="header-navigation">
          <Tabs
            type="navigation"
            value={page}
            onChange={(val) => {
              setPage(val);
              navigate(`?current=${val}`);
              onPageChange?.(val);
            }}
            data={[
              { label: 'Products', value: 'products' },
              { label: 'Ad formats', value: 'adFormats' },
              { label: 'Help center', value: 'support' },
            ]}
          />
        </div>
        <div className="options">
          <Button type="default"
            className={classNames('!mr-3r', {
              'invisible': page === 'getInTouch',
            })}
            onClick={() => {
              localStorage.removeItem(SET_GET_IN_TOUCH);
              navigate('?current=getInTouch');
              onPageChange?.('getInTouch');
            }}
          >
            Get in touch
          </Button>
          <DropMenu
            data={[
              { value: 'userGrowth', label: (<div className="odc-platform-item"><div className="odc-platform-icon"><ReportUp /></div>User Growth</div>) },
              { value: 'monetization', label: (<div className="odc-platform-item"><div className="odc-platform-icon"><Income /></div>Monetization</div>) },
            ]}
            offsetData={{
              top: 0,
            }}
            dropdownPortalClassName="odc-login-dropmenu"
            onClick={(value: number | string) => {
              onLoginClick?.(value as string);
            }}
            trigger="hover"
          >
            <Button type="primary">
              Log in
            </Button>
          </DropMenu>
        </div>
        <button
          className={classNames('mobile-menu-btn', { open: mobileMenuOpen })}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className="hamburger-line" />
          <span className="hamburger-line" />
          <span className="hamburger-line" />
        </button>
      </div>
      {/* Mobile navigation drawer */}
      <div className={classNames('odc-mobile-nav-drawer', { open: mobileMenuOpen })}>
        <div className="mobile-nav-items">
          {[
            { label: 'Products', value: 'products' },
            { label: 'Ad formats', value: 'adFormats' },
            { label: 'Help center', value: 'support' },
          ].map(item => (
            <div
              key={item.value}
              className={classNames('mobile-nav-item', { active: page === item.value })}
              onClick={() => handleMobileNavClick(item.value)}
            >
              {item.label}
            </div>
          ))}
        </div>
        <div className="mobile-nav-actions">
          <Button
            type="default"
            className="mobile-get-in-touch-btn"
            onClick={() => {
              localStorage.removeItem(SET_GET_IN_TOUCH);
              handleMobileNavClick('getInTouch');
            }}
          >
            Get in touch
          </Button>
          <Button
            type="primary"
            onClick={() => {
              onLoginClick?.('userGrowth');
              setMobileMenuOpen(false);
            }}
          >
            Log in
          </Button>
        </div>
      </div>
      <div className={classNames('odc-mobile-nav-overlay', { open: mobileMenuOpen })} onClick={() => setMobileMenuOpen(false)} />
    </>
  );
};

export default Header;
